// UserDetails/index.jsx

import React from "react";
import { useParams } from "react-router-dom";
import { useUserDetails } from "../../hooks/useUserDetails";
import UserProfile from "./UserProfile";
import ContactInfo from "./ContactInfo";
import DocumentSection from "./DocumentSection";
import BankDetails from "./BankDetails";
import AccountInfo from "./AccountInfo";
import ShopList from "./Shops/ShopList";
import ShopDetails from "./Shops/ShopDetails";
import UserStatusActions from "./StatusActions/UserStatusActions";

export default function UserDetails() {
  const { userId } = useParams();
  const {
    userData,
    activeShop,
    setActiveShop,
    loading,
    error,
    updating,
    changeUserStatus,
    resubmitDocuments,
    changeShopStatus,
    updateUserProfileData,
    updateUserContactInfo,
    updateShopData,
    updateShopBankInfo,
    updateShopMediaFiles,
    clearError,
  } = useUserDetails(userId);

  if (loading)
    return (
      <div className="w-full h-40 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-screen-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">Error Loading User Details</div>
            <div className="text-red-500 mb-4">{error}</div>
            <button
              onClick={clearError}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );

  if (!userData)
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-screen-2xl mx-auto">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <div className="text-gray-600 text-lg">No user data found</div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-screen-2xl mx-auto">
        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-red-600 font-medium">{error}</div>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700 text-sm font-medium"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {updating && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">Updating...</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* --- LEFT COLUMN --- */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-white rounded-lg shadow-sm p-5">
              <UserProfile
                data={userData}
                onUpdate={updateUserProfileData}
              />
              <ContactInfo
                data={userData}
                onUpdate={updateUserProfileData}
                onUpdateContact={updateUserContactInfo}
              />
            </div>

            <div className="bg-white rounded-lg shadow-sm p-5">
              <h2 className="text-base font-semibold text-gray-700 mb-3">
                Verify ShopOwner Profile:
              </h2>
              <UserStatusActions
                status={userData.status}
                onChange={changeUserStatus}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-6">
              <DocumentSection
                docs={userData.docs}
                onResubmit={resubmitDocuments}
              />
              <div className="flex flex-col gap-6">
                <BankDetails
                  shop={activeShop}
                  onUpdate={updateShopBankInfo}
                />
                <AccountInfo data={userData} />
              </div>
            </div>
          </div>

          {/* --- RIGHT COLUMN --- */}
          <div className="lg:col-span-3">
            {userData.shop && userData.shop.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm h-full flex flex-col">
                <h2 className="p-4 border-b text-lg font-semibold text-gray-800">
                  Shops ({userData.shop.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 h-full min-h-[600px]">
                  <div className="md:col-span-1 border-r border-gray-200 overflow-y-auto">
                    <ShopList
                      shops={userData.shop}
                      activeShop={activeShop}
                      onSelect={setActiveShop}
                    />
                  </div>
                  <div className="md:col-span-2 p-4 overflow-y-auto">
                    {activeShop ? (
                      <ShopDetails
                        shop={activeShop}
                        onStatusChange={changeShopStatus}
                        onUpdate={updateShopData}
                        onUpdateMedia={updateShopMediaFiles}
                        userId={userId}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        Select a shop to see details
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm h-full flex items-center justify-center text-gray-500">
                No shops found for this user.
              </div>
            )}
          </div>
        </div>

        {/* <div className="flex justify-end mt-6">
          <button
            className="px-5 py-2 text-gray-700 font-medium text-sm hover:bg-gray-200 rounded-md transition-colors"
            onClick={() => window.close()}
          >
            Close
          </button>
        </div> */}
      </div>
    </div>
  );
}
